import { Box, Stack, Typography, useTheme } from '@mui/material'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { OlexFileUploader } from 'src/components/OlexFileUploader'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { IMooringType, IPositionType, IPositionWithDrawing } from 'src/generated-types'
import { useTranslation } from 'react-i18next'
import { FormCheckbox, FormSelectField, FormTextField } from 'src/components/FormCore'
import { useGetProjectMapSVG } from 'src/api/project'
import { UseFormReturn } from 'react-hook-form'
import PositionCountSection from './components/positions/PositionCountSection'
import { useDialogCtx } from 'src/hooks/context-hooks'
import ProjectPositionMapDialog from './ProjectPositionMapDialog'

import ConfigurePositionsDialog from './components/positions/ConfigurePositionsDialog'
import { IProjectInfo } from 'src/generated-types/project'
import { useSetupOlexRevision } from './versions/useSetupOlexRevision'

interface SetupFacilityFormProps {
  project: IProjectInfo
  form: UseFormReturn<any>
  existingPositions?: IPositionWithDrawing[]
  hasPendingChanges?: boolean
  setHasPendingChanges?: React.Dispatch<React.SetStateAction<boolean>>
  showMapSVG?: boolean
  isConfigurePositions?: boolean
}

const SetupFacilityForm: React.FC<SetupFacilityFormProps> = ({
  project,
  form,
  existingPositions,
  setHasPendingChanges,
  showMapSVG = true,
  isConfigurePositions = true,
}) => {
  const { customerId, localityId, id: projectId } = project
  const [positions, setPositions] = useState<IPositionWithDrawing[]>(existingPositions ?? [])
  const [bridleNames, setBridleNames] = useState<string[]>(form?.getValues('bridleNames') || [])
  const { showDialog } = useDialogCtx()

  const { t: translate } = useTranslation(['position', 'mooring', 'file-upload'])

  const noOfCages = form.watch('noOfCages') || 0
  const noOfRows = form.watch('noOfRows') || 0
  const noOfPens = form.watch('noOfPens') || 0
  const projectType = form.watch('type') || 0
  const serviceRow = !!form.watch('serviceRow')
  const isFrameCage = projectType === IMooringType.FrameCage
  const isServiceCorridorApplicable = isFrameCage && Number(noOfRows) > 1

  useEffect(() => {
    if (!isFrameCage && form.getValues('serviceRow')) form.setValue('serviceRow', false)
  }, [isFrameCage, form])

  const projectTypes = useMemo(() => {
    return Object.values(IMooringType)
      .filter(v => isNaN(Number(v)))
      .map(t => IMooringType[t])
  }, [])

  const theme = useTheme()

  const onCancel = useCallback(() => {
    form.setValue('noOfCages', project.noOfCages)
    form.setValue('noOfPens', project.noOfPens)
    form.setValue('noOfRows', project.noOfRows)
    form.setValue('serviceRow', project.serviceRow ?? false)
  }, [project, form])

  const handleConfirm = useCallback((updatedPositions: IPositionWithDrawing[]) => {
    setPositions(updatedPositions)
  }, [])

  const revisionOlex = useSetupOlexRevision(project, form, setPositions)

  const handleConfirmDialog = useCallback(
    (updatedPositions: IPositionWithDrawing[]) => {
      showDialog(
        ProjectPositionMapDialog,
        {
          componentProps: {
            customerId,
            localityId,
            projectId,
            updatedPositions,
            setPositions,
            projectType,
            noOfCages,
            noOfRows,
            noOfPens,
            serviceRow,
            onCancel,
          },
          opts: {
            maxWidth: '80vw',
            fullWidth: true,
          },
        },
        undefined,
        true,
      )
    },
    [customerId, localityId, projectId, showDialog, noOfCages, noOfPens, noOfRows, projectType, serviceRow, onCancel],
  )

  const positionCounts = useMemo(() => {
    return {
      noOfFrameLines: positions.filter(p => p.type === IPositionType.FrameLines).length,
      noOfCagePositions: positions.filter(p => p.type === IPositionType.Cage).length,
      noOfBridles: positions.filter(p => p.type === IPositionType.Bridle).length,
      noOfCorners: positions.filter(p => p.type === IPositionType.Corner).length,
      noOfMooringLines: positions.filter(p => p.type === IPositionType.MooringLine).length,
    }
  }, [positions])

  useEffect(() => {
    form.setValue('positions', positions)
  }, [positions, form])

  const { data } = useGetProjectMapSVG(customerId, localityId, projectId, (positions?.length ?? 0) > 0)
  const getSelectedProjectTypeIndex = () => {
    const currentType = form.watch('type')
    return projectTypes.indexOf(currentType)
  }

  return (
    <Box paddingY={2}>
      <Typography paddingX={1} paddingBottom={2} variant="body1">
        {translate('setup-facility', { ns: 'projects' })}
      </Typography>
      <Stack direction={'row'} alignItems={'flex-start'}>
        <FormTextField
          type="number"
          name="gridLineWidthInMeters"
          label={translate('grid-line-width', { ns: 'projects' })}
          inputProps={{ min: 0, step: 'any' }}
          sx={{ width: '100%' }}
        />
        <FormTextField
          type="number"
          name="gridLineHeightInMeters"
          label={translate('grid-line-height', { ns: 'projects' })}
          inputProps={{ min: 0, step: 'any' }}
          sx={{ width: '100%' }}
        />
      </Stack>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
        <FormSelectField
          name="type"
          data={projectTypes}
          label={translate('type', { ns: 'projects' })}
          getOptionLabel={option => translate(IMooringType[option], { ns: 'mooring' })}
          renderOption={(props: any, option) => (
            <Box {...props} key={option}>
              {translate(IMooringType[option], { ns: 'mooring' })}
            </Box>
          )}
          sx={{ width: '100%' }}
        />
        {!(projectType === IMooringType.Barge) && (
          <>
            <FormTextField
              type="number"
              name="noOfCages"
              label={translate('noOfCages', { ns: 'mooring' })}
              key={'noOfCages'}
              disabled={projectType === null}
              inputProps={{ min: 1 }}
              hideErrorMessage
            />
            {projectType === IMooringType.LadderCage ? (
              <FormTextField
                type="number"
                name="noOfPens"
                label={translate('noOfPens', { ns: 'mooring' })}
                key={'noOfPens'}
                disabled={projectType === null}
                InputProps={{ inputProps: { min: 0 } }}
              />
            ) : (
              <>
                {isFrameCage && (
                  <FormTextField
                    type="number"
                    name="noOfPens"
                    label={translate('noOfPens', { ns: 'mooring' })}
                    key={'noOfPensFrameCage'}
                    inputProps={{ min: 1 }}
                  />
                )}
                <FormTextField
                  type="number"
                  name="noOfRows"
                  label={translate('noOfRows', { ns: 'mooring' })}
                  key={'noOfRows'}
                  disabled={projectType === null}
                  inputProps={{ min: 1 }}
                  hideErrorMessage
                />
              </>
            )}
          </>
        )}
      </Stack>
      {isFrameCage && (
        <FormCheckbox
          name="serviceRow"
          label={translate('isServiceCorridorAvailable', { ns: 'mooring' })}
          disabled={!isServiceCorridorApplicable}
        />
      )}
      {projectType === IMooringType.Barge && getSelectedProjectTypeIndex() !== -1 && (
        <FormSelectField
          multiple
          name="bridleNames"
          data={Array.from({ length: 20 }, (_, i) => String(i + 501)) || []}
          defaultValue={[]}
          label={translate('bridle-names', { ns: 'mooring' })}
          getOptionLabel={option => option}
          renderOption={(props: any, option) => (
            <Box {...props} key={option}>
              {option}
            </Box>
          )}
          groupBy={(option: any) => option.type}
          isOptionEqualToValue={(o, v) => o === v}
          key={'bridleNames'}
          sx={{ width: '100%' }}
          onChange={(_, value) => {
            setBridleNames(value as string[])
            form.setValue('bridleNames', value)
          }}
        />
      )}
      <OlexFileUploader
        isDialog={false}
        customerId={customerId}
        handleConfirm={data => handleConfirmDialog(data)}
        pop={() => true}
        projectId={projectId}
        projectType={projectType}
        noOfCages={noOfCages}
        noOfRows={noOfRows}
        noOfPens={noOfPens}
        serviceRow={serviceRow && isServiceCorridorApplicable}
        bridleNames={bridleNames}
        localityId={localityId}
        selectedProjectTypeIndex={getSelectedProjectTypeIndex()}
        onBeforeUpload={revisionOlex.onBeforeUpload}
      />
      <PositionCountSection
        handleConfirm={handleConfirm}
        customerId={customerId}
        projectId={projectId}
        localityId={localityId}
        positionCounts={positionCounts}
        noOfCages={noOfCages}
        noOfRows={noOfRows}
        serviceRow={serviceRow && isServiceCorridorApplicable}
        projectType={projectType}
      />
      {isConfigurePositions && (
        <ConfigurePositionsDialog
          project={project}
          pop={() => true}
          projectForm={form}
          setHasPendingChanges={setHasPendingChanges}
          olexRequest={revisionOlex.olexRequest}
          onOlexApplied={revisionOlex.onApplied}
        />
      )}
      {showMapSVG && positions && positions.length > 0 && (
        <Box
          padding={1}
          margin={1}
          sx={{
            border: '1px solid',
            borderRadius: '4px',
            borderColor: getThemeColor(theme, MUIThemeColors.inputBorder),
          }}
        >
          <Box>
            <div dangerouslySetInnerHTML={{ __html: data || '' }} style={{ width: '100%', borderRadius: '6px' }} />
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default SetupFacilityForm
